<template>
  <div class="filter-panel" @click.stop>
    <p class="filter-panel__title">{{ $t('TICKET.FILTER.TITLE') }}</p>

    <div class="filter-panel__content">
      <div class="filter-panel__field">
        <label class="filter-panel__label">{{ $t('TICKET.FILTER.SEARCH') }}</label>
        <input
          v-model="pendingSearchQuery"
          type="text"
          class="filter-panel__text-input"
          :placeholder="$t('TICKET.FILTER.SEARCH_PLACEHOLDER')"
        />
      </div>

      <div class="filter-panel__field">
        <label class="filter-panel__label">{{ $t('TICKET.FILTER.DATE_FIELD') }}</label>
        <select v-model="pendingDateField" class="filter-panel__select-input">
          <option value="created_at">{{ $t('TICKET.FILTER.DATE_CREATED') }}</option>
          <option value="updated_at">{{ $t('TICKET.FILTER.DATE_UPDATED') }}</option>
        </select>
        <div class="filter-panel__date-row">
          <input v-model="pendingStartDate" type="date" class="filter-panel__date-input" />
          <span class="filter-panel__date-separator">—</span>
          <input v-model="pendingEndDate" type="date" class="filter-panel__date-input" />
        </div>
      </div>

      <div class="filter-panel__field">
        <label class="filter-panel__label">{{ $t('TICKET.FORM.LABEL.LABEL') }}</label>
        <multiselect
          v-model="pendingLabels"
          :options="labels"
          track-by="id"
          label="title"
          :multiple="true"
          :close-on-select="false"
          :clear-on-select="false"
          :hide-selected="true"
          :searchable="true"
          :show-labels="false"
          open-direction="below"
          :placeholder="$t('TICKET.FILTER.ALL_LABELS')"
          :select-label="$t('FORMS.MULTISELECT.CLICK_TO_SELECT')"
          :deselect-label="$t('FORMS.MULTISELECT.CLICK_TO_REMOVE')"
        />
      </div>

      <div class="filter-panel__field">
        <label class="filter-panel__label">{{ $t('TICKET.STATUS.LABEL') }}</label>
        <multiselect
          v-model="pendingStatus"
          :options="statusOptions"
          :multiple="true"
          track-by="value"
          label="label"
          :searchable="false"
          :close-on-select="false"
          :show-labels="false"
          open-direction="below"
          :placeholder="$t('TICKET.FILTER.SELECT_STATUS')"
        />
      </div>

      <div v-if="!hideAgentFilter" class="filter-panel__field">
        <label class="filter-panel__label">{{ $t('CRM_DASHBOARD.FILTERS.AGENT') }}</label>
        <multiselect
          v-model="pendingAgents"
          :options="agentOptions"
          :multiple="true"
          track-by="id"
          label="name"
          :searchable="true"
          :close-on-select="false"
          :show-labels="false"
          open-direction="below"
          :placeholder="$t('CRM_DASHBOARD.FILTERS.ALL_AGENTS')"
        />
      </div>

      <div class="filter-panel__field">
        <label class="filter-panel__label">{{ $t('TICKET.FORM.PRIORITY.LABEL') }}</label>
        <multiselect
          v-model="pendingPriority"
          :options="priorityOptions"
          :multiple="true"
          track-by="value"
          label="label"
          :searchable="false"
          :close-on-select="false"
          :show-labels="false"
          open-direction="below"
          :placeholder="$t('TICKET.FILTER.ALL_PRIORITIES')"
        />
      </div>
    </div>

    <div class="filter-panel__actions">
      <button class="filter-panel__btn filter-panel__btn--clear" @click="handleClear">
        {{ $t('TICKET.FILTER.CLEAR') }}
      </button>
      <button class="filter-panel__btn filter-panel__btn--apply" @click="handleApply">
        {{ $t('TICKET.FILTER.APPLY') }}
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'TicketFilterPanel',
  props: {
    statusOptions: { type: Array, default: () => [] },
    agentOptions: { type: Array, default: () => [] },
    priorityOptions: { type: Array, default: () => [] },
    appliedStatus: { type: Array, default: () => [] },
    appliedSearchQuery: { type: String, default: '' },
    appliedDateField: { type: String, default: 'created_at' },
    appliedStartDate: { type: String, default: '' },
    appliedEndDate: { type: String, default: '' },
    appliedAgentIds: { type: Array, default: () => [] },
    appliedPriority: { type: Array, default: () => [] },
    appliedLabelIds: { type: Array, default: () => [] },
    hideAgentFilter: { type: Boolean, default: false },
  },
  data() {
    return {
      pendingSearchQuery: '',
      pendingDateField: 'created_at',
      pendingStatus: [],
      pendingStartDate: '',
      pendingEndDate: '',
      pendingAgents: [],
      pendingPriority: [],
      pendingLabels: [],
    };
  },
  computed: {
    ...mapGetters({
      labels: 'labelsCrm/getLabels',
    }),
  },
  watch: {
    agentOptions() {
      if ((this.appliedAgentIds || []).length) {
        this.syncPendingAgents();
      }
    },
    statusOptions() {
      if ((this.appliedStatus || []).length) {
        this.pendingStatus = (this.appliedStatus || [])
          .map(v => this.statusOptions.find(o => o.value === v))
          .filter(Boolean);
      }
    },
    labels() {
      if ((this.appliedLabelIds || []).length) {
        this.pendingLabels = this.mapAppliedLabels();
      }
    },
  },
  mounted() {
    this.syncPendingFromApplied();
    this.$store.dispatch('labelsCrm/get');
  },
  methods: {
    syncPendingFromApplied() {
      this.pendingSearchQuery = this.appliedSearchQuery || '';
      this.pendingDateField = this.appliedDateField || 'created_at';
      this.pendingStatus = (this.appliedStatus || [])
        .map(v => this.statusOptions.find(o => o.value === v))
        .filter(Boolean);
      this.pendingStartDate = this.appliedStartDate || '';
      this.pendingEndDate = this.appliedEndDate || '';
      this.syncPendingAgents();
      this.pendingPriority = (this.appliedPriority || [])
        .map(v => this.priorityOptions.find(p => p.value === v))
        .filter(Boolean);
      this.pendingLabels = this.mapAppliedLabels();
    },
    syncPendingAgents() {
      this.pendingAgents = (this.appliedAgentIds || [])
        .map(id => this.agentOptions.find(a => String(a.id) === String(id)))
        .filter(Boolean);
    },
    mapAppliedLabels() {
      const labelList = this.labels || [];
      return (this.appliedLabelIds || [])
        .map(id => labelList.find(l => String(l.id) === String(id)))
        .filter(Boolean);
    },
    handleApply() {
      this.$emit('filter-apply', {
        searchQuery: (this.pendingSearchQuery || '').trim(),
        dateField: this.pendingDateField || 'created_at',
        status: (this.pendingStatus || []).map(s => s.value),
        startDate: this.pendingStartDate || '',
        endDate: this.pendingEndDate || '',
        agentIds: this.hideAgentFilter
          ? []
          : (this.pendingAgents || []).map(a => a.id),
        priority: (this.pendingPriority || []).map(p => p.value),
        labelIds: (this.pendingLabels || []).map(l => l.id),
      });
    },
    handleClear() {
      this.pendingSearchQuery = '';
      this.pendingDateField = 'created_at';
      this.pendingStatus = [];
      this.pendingStartDate = '';
      this.pendingEndDate = '';
      this.pendingAgents = [];
      this.pendingPriority = [];
      this.pendingLabels = [];
      this.$emit('clear');
    },
  },
};
</script>

<style lang="scss" scoped>
.filter-panel {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 1000;
  background: var(--white);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-normal);
  box-shadow: var(--shadow-medium, 0 4px 16px rgba(0, 0, 0, 0.12));
  padding: var(--space-normal);
  min-width: 30rem;
  max-height: calc(100vh - 8rem);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &__title {
    font-size: var(--font-size-small);
    font-weight: var(--font-weight-bold);
    color: var(--b-800);
    margin: 0 0 var(--space-normal) 0;
    flex-shrink: 0;
  }

  &__content {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: var(--space-micro);
  }

  &__field {
    margin-bottom: var(--space-normal);
  }

  &__label {
    display: block;
    font-size: var(--font-size-small);
    font-weight: var(--font-weight-medium);
    color: var(--b-600);
    margin-bottom: var(--space-smaller);
  }

  &__text-input,
  &__select-input {
    width: 100%;
    margin: 0 0 var(--space-smaller) 0 !important;
    height: 3.4rem;
    border-radius: var(--border-radius-normal);
    border: 1px solid var(--color-border);
    font-size: var(--font-size-small);
    background-color: var(--white);
    padding: 0 var(--space-smaller);
    &:focus {
      outline: none;
      border-color: var(--w-500);
    }
  }

  &__date-row {
    display: flex;
    align-items: center;
    gap: var(--space-smaller);
  }

  &__date-separator {
    color: var(--color-border);
    font-size: var(--font-size-small);
  }

  &__date-input {
    flex: 1;
    margin: 0 !important;
    height: 3.4rem;
    border-radius: var(--border-radius-normal);
    border: 1px solid var(--color-border);
    font-size: var(--font-size-small);
    background-color: var(--white);
    padding: 0 var(--space-smaller);
    &:focus {
      outline: none;
      border-color: var(--w-500);
    }
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--space-small);
    margin-top: var(--space-small);
    padding-top: var(--space-small);
    border-top: 1px solid var(--color-border-light);
    flex-shrink: 0;
    background: var(--white);
  }

  &__btn {
    border-radius: var(--border-radius-normal);
    font-size: var(--font-size-small);
    font-weight: var(--font-weight-medium);
    cursor: pointer;
    padding: var(--space-smaller) var(--space-normal);
    border: 1px solid transparent;
    transition: background-color 0.15s;

    &--clear {
      background: var(--white);
      border-color: var(--color-border);
      color: var(--b-600);
      &:hover {
        background: var(--b-50);
      }
    }

    &--apply {
      background: var(--w-500);
      color: var(--white);
      &:hover {
        background: var(--w-600);
      }
    }
  }
}
</style>
